import { createFileRoute } from '@tanstack/react-router'
import { docsUrl } from '@/lib/docs-url'
import { ShapeGrid } from '@/components/landing/shape-grid'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ThemeSwitch } from '@/components/theme-switch'
import { LanguageSwitch } from '@/components/language-switch'
import { AnimatedX } from '@/components/animated-x'
import { TrafficDemo } from '@/components/landing/traffic-demo'
import { ForwardDemo } from '@/components/landing/forward-demo'
import { useTranslation } from 'react-i18next'
import {
  Server,
  Shield,
  Network,
  Zap,
  BookOpen,
  ArrowRight,
  Globe,
  Lock,
  Activity,
  Users,
  ChevronDown,
  Github,
  Send,
  Sparkles,
  FileText,
  ShoppingCart,
  Cat,
  Shuffle,
  Waypoints,
} from 'lucide-react'

export const Route = createFileRoute('/')({
  component: XHomePage,
})

const featureDefs = [
  { icon: Globe, key: 'remoteServer' },
  { icon: Network, key: 'xrayInbound' },
  { icon: Waypoints, key: 'forward' },
  { icon: Lock, key: 'certificates' },
  { icon: Activity, key: 'trafficMonitor' },
  { icon: Users, key: 'userPackage' },
  { icon: Globe, key: 'multiFormat' },
  { icon: Zap, key: 'autoSync' },
]

const comparisonKeys = [
  { key: 'subscriptionMgmt', mmw: true, mmwx: true },
  { key: 'multiClient', mmw: true, mmwx: true },
  { key: 'trafficProbe', mmw: true, mmwx: true },
  { key: 'customRulesTemplates', mmw: true, mmwx: true },
  { key: 'remoteServer', mmw: false, mmwx: true },
  { key: 'xrayInOut', mmw: false, mmwx: true },
  { key: 'acmeCert', mmw: false, mmwx: true },
  { key: 'connectivityTest', mmw: false, mmwx: true },
]

const quickLinkDefs = [
  { key: 'quickStart', href: '/docs/quick-start', icon: Zap },
  { key: 'install', href: '/docs/install-direct', icon: Server },
  { key: 'protocols', href: '/docs/protocol-matrix', icon: Network },
  { key: 'fullDocs', href: '/docs', icon: BookOpen },
]

// 协议矩阵(docs 的 protocol-matrix 页)里的组合数。加减协议或组合时,这个数、下面的 protocolDefs
// 和 landing.json 里写着数字的几处文案要一起改。
const PROTOCOL_COMBOS = 27

// Miu 的标志(μ 加一对猫耳),与面板里的 miu-mark.svg 同一份路径;跟随文字颜色,明暗主题都能用。
function MiuMark({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="120 90 272 332" role="img" aria-label="Miu" className={className}>
      <g transform="translate(-13 -14)" fill="none" stroke="currentColor" strokeWidth="40" strokeLinecap="round" strokeLinejoin="round">
        <path d="M196 204V400" />
        <path d="M196 204V296a60 60 0 0 0 120 0V204" />
        <path d="M316 296q0 52 44 52" />
        <path d="M182 204L182 134L210 204Z" fill="currentColor" strokeWidth="12" />
        <path d="M330 204L330 134L302 204Z" fill="currentColor" strokeWidth="12" />
      </g>
    </svg>
  )
}

// 当前支持的入站协议(权威顺序参照文档侧栏「协议参考」分组);各卡链到对应协议文档专页。
// 首页的「协议支持」数字直接取这张表的长度。
//
// logo 是各项目自己的标志(文件在 public/images/protocols/,来源见卡片下方的说明文案):
//   - VLESS 用 Xray 的,VMess 用 V2Fly 的,Hysteria2 用 Hysteria 的;logoDark 是暗色主题下的版本。
//   - Snell 用 Surge 知识库(Snell 的官方文档所在)的图标。
//   - AnyTLS / Mieru / Trojan 没有可用的官方标志,保留图标。
//   - WireGuard 的商标政策不允许把它的标志用在销售产品或服务的页面上,只用名称,不放标志。
const protocolDefs: {
  key: string
  name: string
  href: string
  icon: typeof Shield
  color: string
  badge?: string
  logo?: string
  logoDark?: string
  mark?: typeof MiuMark
}[] = [
  { key: 'vless', name: 'VLESS', href: '/docs/protocol-vless', icon: Shield, color: 'text-purple-500', logo: '/images/protocols/xray-light.svg', logoDark: '/images/protocols/xray-dark.svg' },
  { key: 'vmess', name: 'VMess', href: '/docs/protocol-vmess', icon: Lock, color: 'text-blue-500', logo: '/images/protocols/v2fly.png' },
  { key: 'trojan', name: 'Trojan', href: '/docs/protocol-trojan', icon: Activity, color: 'text-red-500' },
  { key: 'shadowsocks', name: 'Shadowsocks', href: '/docs/protocol-shadowsocks', icon: Network, color: 'text-green-500', logo: '/images/protocols/shadowsocks.png' },
  { key: 'hysteria2', name: 'Hysteria2', href: '/docs/protocol-hysteria2', icon: Zap, color: 'text-indigo-500', logo: '/images/protocols/hysteria-light.svg', logoDark: '/images/protocols/hysteria-dark.svg' },
  { key: 'anytls', name: 'AnyTLS', href: '/docs/protocol-anytls', icon: Globe, color: 'text-teal-500' },
  { key: 'snell', name: 'Snell', href: '/docs/protocol-snell', icon: Server, color: 'text-lime-500', logo: '/images/protocols/snell.png' },
  { key: 'mieru', name: 'Mieru', href: '/docs/protocol-mieru', icon: Shuffle, color: 'text-amber-500' },
  { key: 'miu', name: 'Miu', href: '/docs/protocol-miu', icon: Cat, color: 'text-pink-500', badge: 'NEW', mark: MiuMark },
  { key: 'wireguard', name: 'WireGuard', href: '/docs/protocol-wireguard', icon: Waypoints, color: 'text-sky-500', badge: 'NEW' },
]

function XHomePage() {
  const { t } = useTranslation('landing')

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b border-[color:rgba(241,140,110,0.22)] bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-16 items-center justify-between px-4 sm:px-6 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <img
              src="/images/logo.webp"
              alt={t('xHome.logoAlt')}
              className="h-10 w-10 border-2 border-[color:rgba(241,140,110,0.4)] shadow-[4px_4px_0_rgba(0,0,0,0.2)]"
            />
            <span className="pixel-text text-base">
              <span className="text-primary">{t('common:brand')}</span>
              <AnimatedX size="sm" className="ml-1" />
            </span>
          </div>
          <nav className="flex items-center gap-2 sm:gap-4">
            <a
              href={docsUrl('/docs/tutorial')}
              className="pixel-button inline-flex items-center gap-2 px-3 py-2 h-9 text-sm font-semibold uppercase tracking-widest bg-background/75 text-foreground border-[color:rgba(137,110,96,0.45)] hover:bg-accent/35 hover:text-accent-foreground transition-all"
            >
              <BookOpen className="size-4" />
              <span className="hidden sm:inline">{t('xHome.navDocs')}</span>
            </a>
            <a
              href={docsUrl('/docs/changelog')}
              className="pixel-button inline-flex items-center gap-2 px-3 py-2 h-9 text-sm font-semibold uppercase tracking-widest bg-background/75 text-foreground border-[color:rgba(137,110,96,0.45)] hover:bg-accent/35 hover:text-accent-foreground transition-all"
            >
              <FileText className="size-4" />
              <span className="hidden sm:inline">{t('xHome.navChangelog')}</span>
            </a>
            <a
              href="https://license.miaomiaowux.com/pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="pixel-button inline-flex items-center gap-2 px-3 py-2 h-9 text-sm font-semibold uppercase tracking-widest bg-primary/90 text-primary-foreground border-[color:rgba(137,110,96,0.45)] hover:bg-primary transition-all"
            >
              <ShoppingCart className="size-4" />
              <span className="hidden sm:inline">{t('xHome.navPricing')}</span>
            </a>
            <a
              href="https://github.com/iluobei/miaomiaowu"
              target="_blank"
              rel="noopener noreferrer"
              className="pixel-button inline-flex items-center gap-2 px-3 py-2 h-9 text-sm font-semibold uppercase tracking-widest bg-background/75 text-foreground border-[color:rgba(137,110,96,0.45)] hover:bg-accent/35 hover:text-accent-foreground transition-all"
            >
              <Github className="size-4" />
              <span className="hidden sm:inline">GitHub</span>
            </a>
            <a
              href="https://t.me/miaomiaowux"
              target="_blank"
              rel="noopener noreferrer"
              className="pixel-button inline-flex items-center justify-center h-9 w-9 px-2 py-2 bg-background/75 text-foreground border-[color:rgba(137,110,96,0.45)] hover:bg-accent/35 transition-all"
              aria-label="Telegram"
            >
              <Send className="size-4" />
            </a>
            <LanguageSwitch />
            <ThemeSwitch />
          </nav>
        </div>
      </header>

      <section className="relative overflow-hidden min-h-[80vh] flex items-center justify-center px-4 sm:px-6 py-12 sm:py-20">
        {/* 移动方格网格背景(ReactBits ShapeGrid),样式见 styles/index.css 的 .mmwx-grid-ripple */}
        <div className="mmwx-grid-ripple" data-shape-grid aria-hidden="true" />
        <ShapeGrid />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <Sparkles className="size-4" />
            {t('xHome.advancedBadge')}
          </div>

          <h1 className="pixel-text text-4xl sm:text-5xl md:text-6xl font-bold mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
            <span className="text-primary">{t('common:brand')}</span>
            <AnimatedX size="lg" className="ml-2" />
          </h1>

          <p className="text-xl sm:text-2xl md:text-3xl text-foreground/90 mb-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
            {t('xHome.slogan')}
          </p>

          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-10 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
            {t('xHome.description')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-400">
            <a href={docsUrl('/docs/quick-start')}>
              <Button size="lg" className="pixel-button w-full sm:w-auto px-8 py-6 text-lg font-semibold">
                <Zap className="size-5 mr-2" />
                {t('xHome.quickStart')}
              </Button>
            </a>
            <a href={docsUrl('/docs')}>
              <Button variant="outline" size="lg" className="pixel-button w-full sm:w-auto px-8 py-6 text-lg font-semibold">
                <BookOpen className="size-5 mr-2" />
                {t('xHome.viewDocs')}
              </Button>
            </a>
          </div>

          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-500">
            <div className="pixel-card p-4 text-center">
              <div className="text-2xl sm:text-3xl font-bold text-primary">{PROTOCOL_COMBOS}</div>
              <div className="text-sm text-muted-foreground">{t('xHome.stats.protocolCombos')}</div>
            </div>
            <div className="pixel-card p-4 text-center">
              <div className="text-2xl sm:text-3xl font-bold text-primary">{protocolDefs.length}</div>
              <div className="text-sm text-muted-foreground">{t('xHome.stats.protocols')}</div>
            </div>
            <div className="pixel-card p-4 text-center">
              <div className="text-2xl sm:text-3xl font-bold text-primary">12+</div>
              <div className="text-sm text-muted-foreground">{t('xHome.stats.clientFormats')}</div>
            </div>
            <div className="pixel-card p-4 text-center">
              <div className="text-2xl sm:text-3xl font-bold text-primary">4</div>
              <div className="text-sm text-muted-foreground">{t('xHome.stats.connectionModes')}</div>
            </div>
          </div>
        </div>

        <div className="absolute z-10 bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <ChevronDown className="size-6 text-primary" />
          <ChevronDown className="size-6 text-primary/60 -mt-4" />
        </div>
      </section>

      {/* 支持的协议:每种入站协议一张卡片,点进各协议文档专页 */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="pixel-text text-3xl sm:text-4xl font-bold text-primary mb-4">{t('xHome.protocols.heading')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('xHome.protocols.description')}</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {protocolDefs.map((p) => (
              <a key={p.key} href={docsUrl(p.href)}>
                <Card className="pixel-card group hover:scale-[1.02] hover:shadow-[6px_6px_0_rgba(217,119,87,0.25)] transition-all duration-300 h-full cursor-pointer">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="w-12 h-12 pixel-border flex items-center justify-center bg-primary/10 group-hover:bg-primary/20 transition-colors">
                        {p.mark ? (
                          <p.mark className="size-7 text-foreground" />
                        ) : p.logo ? (
                          <>
                            <img src={p.logo} alt={`${p.name} logo`} className={`size-7 object-contain ${p.logoDark ? 'dark:hidden' : ''}`} />
                            {p.logoDark && <img src={p.logoDark} alt="" aria-hidden="true" className="hidden size-7 object-contain dark:block" />}
                          </>
                        ) : (
                          <p.icon className={`size-6 ${p.color}`} />
                        )}
                      </div>
                      {p.badge && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-primary/15 text-primary">
                          {p.badge}
                        </span>
                      )}
                    </div>
                    <CardTitle className="text-lg mt-3 flex items-center gap-2">
                      {p.name}
                      <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed">
                      {t(`xHome.protocols.items.${p.key}`)}
                    </CardDescription>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-muted-foreground">{t('xHome.protocols.credits')}</p>
        </div>
      </section>

      {/* 实时演示:流量信息 mock(网速跳动 + 用户/节点/服务器三维度 + 下钻 Dialog) */}
      <TrafficDemo />

      {/* 实时演示:转发链 mock(指标条 + 网络拓扑 + 待处理 + 链列表) */}
      <ForwardDemo />

      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="pixel-text text-3xl sm:text-4xl font-bold text-primary mb-4">{t('xHome.coreCapabilities')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('xHome.coreCapabilitiesDesc')}
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {featureDefs.map((feature, index) => (
              <Card
                key={feature.key}
                className="pixel-card group hover:scale-[1.02] hover:shadow-[6px_6px_0_rgba(217,119,87,0.25)] transition-all duration-300"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <CardHeader className="pb-3">
                  <div className="w-12 h-12 pixel-border flex items-center justify-center mb-3 bg-primary/10 group-hover:bg-primary/20 transition-colors">
                    <feature.icon className="size-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{t(`xHome.features.${feature.key}.title`)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm leading-relaxed">{t(`xHome.features.${feature.key}.desc`)}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="pixel-text text-3xl sm:text-4xl font-bold text-primary mb-4">{t('xHome.comparisonTitle')}</h2>
          </div>
          <div className="overflow-x-auto pixel-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-4 px-6">{t('xHome.comparisonHeaders.feature')}</th>
                  <th className="text-center py-4 px-6">{t('xHome.comparisonHeaders.mmw')}</th>
                  <th className="text-center py-4 px-6">
                    <span className="font-bold">{t('common:brand')}<AnimatedX size="sm" /></span>
                  </th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                {comparisonKeys.map((row, i) => (
                  <tr key={row.key} className={i < comparisonKeys.length - 1 ? 'border-b' : ''}>
                    <td className="py-3 px-6">{t(`xHome.comparisonRows.${row.key}`)}</td>
                    <td className="text-center py-3 px-6">{row.mmw ? <span className="text-primary">&#10003;</span> : '-'}</td>
                    <td className="text-center py-3 px-6"><span className="text-primary">&#10003;</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="pixel-text text-3xl sm:text-4xl font-bold text-primary mb-4">{t('xHome.quickNav')}</h2>
            <p className="text-lg text-muted-foreground">{t('xHome.quickNavDesc')}</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {quickLinkDefs.map((item) => (
              <a key={item.href} href={docsUrl(item.href)}>
                <Card className="pixel-card group hover:scale-[1.02] hover:shadow-[6px_6px_0_rgba(217,119,87,0.25)] transition-all duration-300 h-full cursor-pointer">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="w-10 h-10 pixel-border flex items-center justify-center bg-primary/10 group-hover:bg-primary/20 transition-colors">
                        <item.icon className="size-5 text-primary" />
                      </div>
                      <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </div>
                    <CardTitle className="text-base mt-3">{t(`xHome.quickLinks.${item.key}.title`)}</CardTitle>
                    <CardDescription>{t(`xHome.quickLinks.${item.key}.desc`)}</CardDescription>
                  </CardHeader>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-[color:rgba(241,140,110,0.22)] bg-background/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/images/logo.webp" alt={t('xHome.logoAlt')} className="h-8 w-8 border-2 border-[color:rgba(241,140,110,0.4)] shadow-[4px_4px_0_rgba(0,0,0,0.2)]" />
              <span className="pixel-text text-sm">
                <span className="text-primary">{t('common:brand')}</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-primary to-amber-500 ml-1">X</span>
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{t('xHome.footerDesc')}</p>
            <div className="flex items-center gap-3">
              <a href="https://github.com/iluobei/miaomiaowu" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors"><Github className="size-4" /></a>
              <a href="https://t.me/miaomiaowux" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors"><Send className="size-4" /></a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
